import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparison.utils';
import { isBuoyDevice } from '../../Buoy/utils/buoyDevice.utils';

/**
 * URLSearchParams transformer: sets ?uid= and optionally ?did=.
 * Pass to React Router's `setParams` as a functional updater.
 *
 * @param {string|URLSearchParams} prev - Current search params from React Router.
 * @param {number|string|null} uid - Device ID to set as ?uid=, or null to remove it.
 * @param {string|null} did - Datasheet ID to set as ?did=, or null to remove it.
 * @returns {URLSearchParams} Updated search params.
 *
 * @example
 * setParams((prev) => setDataSheetURLParams(prev, device.id, did), { replace: true })
 */
export const setDataSheetURLParams = (prev, uid, did) => {
  const searchParams = new URLSearchParams(prev);
  if (uid != null) {
    searchParams.set('uid', String(uid));
  } else {
    searchParams.delete('uid');
  }
  if (did != null) {
    searchParams.set('did', String(did));
  } else {
    searchParams.delete('did');
  }
  return searchParams;
};

/**
 * URLSearchParams transformer: removes ?uid= and ?did=.
 * Pass directly to React Router's `setParams` as a functional updater.
 *
 * @param {string|URLSearchParams} prev - Current search params from React Router.
 * @returns {URLSearchParams} Updated search params with uid and did removed.
 *
 * @example
 * setParams(ClearDatasheetParams, { replace: true })
 */
export const ClearDatasheetParams = (prev) => {
  const searchParams = new URLSearchParams(prev);
  searchParams.delete('uid');
  searchParams.delete('did');
  return searchParams;
};

/**
 * Validates and resolves the ?uid= param in a URL search string against a device list.
 * returns an object with status 'ok' and the matching device if found and valid, or an error status if not.
 *
 * @param {string} search - `window.location.search` or any raw search string.
 * @param {object[]} connectedDevices - Flat device array from RTK Query.
 * @returns {{ status: 'ok', device: object, datasheetType: 'Flood'|'usgs'|'buoy' }
 *   | { status: 'missing' }
 *   | { status: 'invalid' }}
 */
export const resolveDeviceFromSearch = (search, connectedDevices) => {
  const searchParams = new URLSearchParams(search);
  const uid = searchParams.get('uid');
  if (!uid) return { status: 'missing' };

  const device = connectedDevices.find((d) => String(d.id) === String(uid));
  if (!device) return { status: 'invalid' };

  const isUSGS = device.system_type === 'USGS';
  const isFlood = DeviceIsSystemType(device, 'Flood');
  const isBuoy = isBuoyDevice(device);
  if (!isFlood && !isUSGS && !isBuoy) return { status: 'invalid' };

  return { status: 'ok', device, datasheetType: isUSGS ? 'usgs' : isBuoy ? 'buoy' : 'Flood' };
};

/**
 * Resolves ?did= from a URLSearchParams instance against a pre-built did→pageIndex Map.
 * Returns the numeric page index, or null if not found.
 *
 * @param {URLSearchParams} searchParams - Parsed search params to read ?did= from.
 * @param {Map<string, number>} deviceIdToPageMap - Map of did string → page index.
 * @returns {number|null} The matching page index, or null if not found.
 */
export const resolvePageFromDid = (searchParams, deviceIdToPageMap) => {
  const didParam = searchParams.get('did');
  if (!didParam) return null;
  const pageIndex = deviceIdToPageMap.get(String(didParam));
  return typeof pageIndex === 'number' ? pageIndex : null;
};

/**
 * Returns the `did` value for the first entry in a device's data array, or undefined.
 *
 * @param {object} device - Device Object to extract `did` from.
 * @returns {string|undefined} The `did` of the first data entry, or undefined if absent.
 */
export const getPage0Did = (device) => {
  return Array.isArray(device?.data) ? (device.data[0]?.did ?? device.data[0]?.ts) : undefined;
};
